import React from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { H3, H4, Text } from '../Typography';

interface ISectionHeaderProps {
  actions?: React.ReactNode;
  afterHeaderCardRow?: boolean;
  children?: React.ReactNode;
  className?: string;
  dataCy?: string;
  filterActive?: boolean;
  isLoading?: boolean;
  staticSubtitle?: boolean;
  subtitle?: string | React.ReactNode;
  title?: string | React.ReactNode;
  titleSize?: 'default' | 'small';
  value?: any;
  zIndex?: number;
}

const Layout = styled.header<Partial<ISectionHeaderProps>>`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: auto;
  margin-bottom: ${toRem(22)};
  gap: ${toRem(11)};
  transition: margin-bottom ${theme.transition.button.fast};

  ${({ afterHeaderCardRow }) =>
    afterHeaderCardRow &&
    css`
      z-index: ${theme.zIndex.contentHeader + 50};
    `}

  ${({ zIndex }) =>
    zIndex &&
    css`
      z-index: ${zIndex};
    `}

  ${({ filterActive }) =>
    filterActive &&
    css`
      @media (min-width: ${theme.breakpoint.small}px) {
        margin-bottom: ${toRem(22)};
      }
    `}
`;

const TitleContainer = styled.section`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(5.5)};
  width: auto;

  > * {
    line-height: 100%;
  }
`;

const Title = styled(H3)`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;

  span {
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[500]};
  }

  button {
    span {
      color: ${theme.color.white};
    }
  }
`;

const TitleSmall = styled(Text).attrs({
  size: 'small',
  fontWeight: 'semibold',
  as: 'h3',
})`
  margin-bottom: 0;

  span {
    font-weight: ${theme.font.weight.regular};
    color: ${theme.color.neutral[500]};
  }

  button {
    span {
      color: ${theme.color.white};
    }
  }
`;

const Subtitle = styled(H4)`
  margin-bottom: 0;
  color: ${theme.color.neutral[800]};
`;

const TitleActions = styled.div`
  padding-left: 4px;

  button span {
    font-weight: unset;
  }
`;

const Children = styled.section<Partial<ISectionHeaderProps>>`
  position: relative;
  display: flex;
  flex-flow: row wrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
  height: auto;
  gap: ${toRem(11)};
`;

const SectionHeader = ({ actions, afterHeaderCardRow, children, className, dataCy, filterActive, isLoading, staticSubtitle = false, subtitle, title, titleSize, value, zIndex, ...rest }: ISectionHeaderProps) => {
  const TitleContent = isLoading ? (
    <Skeleton width={77} />
  ) : (
    <>
      {title}
      {value && <span className="subtitle">&nbsp;{value}</span>}
      {actions && <TitleActions>{actions}</TitleActions>}
    </>
  );

  const DefaultTitle = <Title>{TitleContent}</Title>;

  const SmallTitle = <TitleSmall>{TitleContent}</TitleSmall>;

  const getTitle = titleSize === 'small' ? SmallTitle : DefaultTitle;

  return (
    <Layout afterHeaderCardRow={afterHeaderCardRow} className={className} filterActive={filterActive} zIndex={zIndex} data-cy={dataCy} {...rest}>
      {title && subtitle ? (
        <TitleContainer>
          {getTitle}
          <Subtitle>{isLoading && !staticSubtitle ? <Skeleton width={55} /> : subtitle}</Subtitle>
        </TitleContainer>
      ) : (
        getTitle
      )}
      {children && <Children>{children}</Children>}
    </Layout>
  );
};

export default SectionHeader;
